import { useEffect, useMemo } from 'react'
import { Empty, Flex, Space, Table, Tag, Tooltip, Typography } from 'antd'
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
} from '@ant-design/icons'
import { useIntl } from 'react-intl'
import { formatEmptyValue } from '../../../utils/common'
import Button from '../../common/Button'
import EllipsisTooltip from '../../common/EllipsisTooltip'
import RecordsCount from '../../common/RecordsCount'
import TableActions from '../../common/TableActions'

const VisualAidTable = ({
  dataSource,
  pagination,
  getVisualAidList,
  onEdit,
  onDelete,
  onDownloadFile,
}) => {
  const intl = useIntl()

  const columns = useMemo(
    () => [
      {
        title: intl.formatMessage({ id: 'visualAid.name' }),
        dataIndex: 'name',
        key: 'name',
        width: '18%',
        sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {formatEmptyValue(text)}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'visualAid.code' }),
        dataIndex: 'code',
        key: 'code',
        width: '12%',
        sorter: (a, b) => (a.code || '').localeCompare(b.code || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {formatEmptyValue(text)}
          </EllipsisTooltip>
        ),
      },
      {
        title: (
          <EllipsisTooltip className="ellipsis">
            {intl.formatMessage({ id: 'visualAid.aidType' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'aid_type',
        key: 'aid_type',
        width: '16%',
        sorter: (a, b) => (a.aid_type || '').localeCompare(b.aid_type || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {formatEmptyValue(text)}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'visualAid.fileName' }),
        dataIndex: 'visual_aid_file_name',
        key: 'visual_aid_file_name',
        align: 'left',
        render: (text, record) => {
          const fileName = formatEmptyValue(text)
          return (
            <EllipsisTooltip className="ellipsis">{fileName}</EllipsisTooltip>
          )
        },
      },
      {
        title: intl.formatMessage({ id: 'visualAid.actions' }),
        key: 'actions',
        width: 210,
        align: 'right',
        render: (_, record) => {
          return (
            <Space size={'small'}>
              {record?.visual_aid_file && (
                <Button
                  type="link"
                  className="link-button"
                  icon={<DownloadOutlined />}
                  onClick={() => onDownloadFile(record)}
                >
                  SVG
                </Button>
              )}

              <TableActions
                actions={[
                  {
                    key: 'edit',
                    label: intl.formatMessage({ id: 'forms.edit' }),
                    icon: <EditOutlined />,
                    onClick: () => onEdit(record),
                  },
                  {
                    key: 'delete',
                    label: intl.formatMessage({ id: 'forms.delete' }),
                    icon: <DeleteOutlined />,
                    danger: true,
                    onClick: () => onDelete(record),
                  },
                ]}
              />
            </Space>
          )
        },
      },
    ],
    [intl, onEdit, onDelete, onDownloadFile]
  )

  const expandable = useMemo(
    () => ({
      columnWidth: 40,
      expandedRowRender: (record) => (
        <div className='sales-code-expanded'>
          <div className="grid-col">
            <div className='header'>
            {intl.formatMessage({ id: 'visualAid.salesCode' })}:{' '}
            </div>
            <div className='cell'>
              {record.sales_codes_data?.map((item) => {
                return <Tag key={`${item.value}-sales-code`}>{item.label}</Tag>
              }) || '-'}
            </div>
          </div>
        </div>
      ),
      rowExpandable: (record) => record.sales_codes_data?.length > 0,
    }),
    [intl]
  )

  return (
    <>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={dataSource}
        showSorterTooltip={false}
        className='visual-aid-table'
        tableLayout="fixed"
        // scroll={dataSource?.length ? {x: 'max-content'} : undefined}
        scroll={dataSource?.length ? {x: 900} : undefined}
        pagination={{
          current: pagination?.currentPage,
          pageSize: 10,
          total: pagination?.totalCount,
          showSizeChanger: false,
          hideOnSinglePage: true,
          showTotal: (total) => <RecordsCount count={total} />,
          onChange: (page) => {
            const offset = (page - 1) * 10
            const url = pagination?.nextPageLink
              ? new URL(pagination?.nextPageLink)
              : new URL(pagination?.prevPageLink)
            url.searchParams.set('offset', offset)
            getVisualAidList?.(url.toString())
          },
        }}
        expandable={expandable}
        locale={{
          emptyText: (
            <div style={{ padding: '40px' }}>
              <Empty description={<RecordsCount count={0} />} />
            </div>
          ),
        }}
      />
      {pagination &&
        pagination?.totalPages <= 1 &&
        pagination?.totalCount > 0 && (
          <Typography.Text>
            <RecordsCount count={pagination?.totalCount} />
          </Typography.Text>
        )}
    </>
  )
}

export default VisualAidTable
